import { useState, useEffect } from 'react';
import { Item, Category, StoreInfo } from '../types';
import { apiService } from '../services/api';

// Generic hook for API calls with loading and error states
function useApi<T>(
  apiCall: () => Promise<T>,
  dependencies: any[] = []
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await apiCall();
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, dependencies);

  return { data, loading, error, refetch: () => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await apiCall();
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }};
}

// Specific hooks for different data types
export function useStoreInfo() {
  return useApi<StoreInfo>(() => apiService.getStoreInfo());
}

export function useCategories() {
  return useApi<Category[]>(() => apiService.getCategories());
}

export function useItems() {
  return useApi<Item[]>(() => apiService.getItems());
}

export function useItemById(id: number) {
  return useApi<Item>(() => apiService.getItemById(id), [id]);
}

export function useItemsByCategory(categoryName: string) {
  return useApi<Item[]>(() => apiService.getItemsByCategory(categoryName), [categoryName]);
}

export function useSearchItems(query: string) {
  return useApi<Item[]>(() => apiService.searchItems(query), [query]);
}

// Hook for managing filtered items
export function useFilteredItems(selectedCategory: string = 'All') {
  const { data: items, loading, error } = useItems();
  const [filteredItems, setFilteredItems] = useState<Item[]>([]);

  useEffect(() => {
    if (items) {
      if (selectedCategory === 'All') {
        setFilteredItems(items);
      } else {
        setFilteredItems(items.filter(item => item.category === selectedCategory));
      }
    }
  }, [items, selectedCategory]);

  return { 
    items: filteredItems, 
    allItems: items, 
    loading, 
    error,
    selectedCategory 
  };
}
